/**
 * AETHER COLOSSUS — Boss da Região 2 (Fase 9). Criatura colossal ancestral com CRYSTAL ARMOR
 * (3 placas que reduzem dano) e mecânica de HOOK: ao errar a Fracture Charge na parede, o
 * Colossus fica em STAGGER com uma placa EXPOSED — o Hook arranca/quebra a placa rápido
 * (o Hook NUNCA puxa o Boss). Ao quebrar todas as placas o núcleo fica exposto (Phase 2).
 *
 * Estados: intro → chase → attack → stagger → phaseTransition → dead.
 * P1: Stone Sweep, Fracture Charge, Shard Rain, Core Pulse.
 * P2 (núcleo exposto): Fracture Leap, Prismatic Line, Core Overload (janela de Hook) + P1 acelerado.
 * Todos os ataques são telegrafados e evitáveis; o Hook recompensa, mas nunca é obrigatório.
 */
import * as THREE from "three";
import { applySkin } from "../enemies/CreatureSkins";
import { applyRim } from "../fx/RimLight";
import { GameConfig } from "../config/GameConfig";
import type { Damageable } from "../combat/Damageable";
import type { HookReaction } from "../player/HookSystem";
import type { TelegraphSystem } from "../fx/Telegraph";
import type { ParticleSystem } from "../fx/Particles";
import type { Boss, BossDebug, PlateInfo } from "./Boss";
import { HealthComponent } from "../combat/HealthComponent";
import { dealDamage } from "../combat/DamageSystem";
import { events } from "../core/Events";
import { dampAngle } from "../utils/math";
import { addContactShadow } from "../enemies/CreatureBuilder";
import { furyMult } from "./Fury"; // 10.9BF

export type ColossusState = "intro" | "chase" | "attack" | "stagger" | "phaseTransition" | "dead";
export type ColossusAttack = "stoneSweep" | "fractureCharge" | "shardRain" | "corePulse" | "fractureLeap" | "prismaticLine" | "coreOverload";

export interface ColossusDeps {
  player: Damageable;
  telegraph: TelegraphSystem;
  particles: ParticleSystem;
  impulse: (strength: number) => void;
}

/** 10.9AP — multiplicadores por instância (vida/dano), como VoidAlpha/RiftSeer. */
export interface ColossusOptions {
  hpMult?: number;
  dmgMult?: number;
}

const cfg = GameConfig.boss.aetherColossus;
const ACCENT = 0x62e8ff;
const CRACK = 0x8fe8ff;

const _v = new THREE.Vector3();
const _tmp = new THREE.Vector3();

interface Plate {
  group: THREE.Group;
  mat: THREE.MeshStandardMaterial;
  broken: boolean;
}

export class AetherColossus implements Boss {
  readonly group = new THREE.Group();
  readonly health: HealthComponent;
  readonly radius = cfg.radius;
  readonly pulled = false; // nunca é puxado

  state: ColossusState = "intro";
  removeRequested = false;

  private stateTimer: number = cfg.introDuration;
  private attackName: ColossusAttack | null = null;
  private atkPhase = 0;
  private atkTimer = 0;
  private phase = 1 as number;
  private restTimer = 0;
  private facing = Math.PI;
  private flash = 0;
  private deathTimer = 0;
  private atkTeleId = 0;

  // 10.9AB — antecipação de movimento (leap/charge/prismatic miram onde o player VAI estar)
  private readonly lastPlayerPos = new THREE.Vector3();
  private readonly playerVel = new THREE.Vector3();
  private velInit = false;

  private readonly cd: Record<ColossusAttack, number> = {
    stoneSweep: 0, fractureCharge: 0, shardRain: 0, corePulse: 0, fractureLeap: 0, prismaticLine: 0, coreOverload: 0,
  };

  // Crystal Armor
  private readonly plates: Plate[] = [];
  private plateProgress = 0; // dano acumulado rumo a quebrar a placa atual
  private exposedTimer = 0; // janela EXPOSED (Hook arranca placa) após charge na parede
  private overloadHookTimer = 0; // janela de Hook durante Core Overload
  private staggered = false;

  // charge / leap / prismatic
  private readonly chargeDir = new THREE.Vector3();
  private chargeTravelled = 0;
  private chargeHitDone = false;
  private readonly targetPos = new THREE.Vector3();
  private readonly leapStart = new THREE.Vector3();
  private leapT = 0;
  private pulseRadius = 0;
  private shardZonesLeft = 0;
  private shardTimer = 0;
  private overloadZoneTimer = 0;
  private overloadZonesLeft = 0;

  private readonly baseDmgMult: number; // 10.9AP — dano por instância (ameaça)
  private get dmgMult(): number { return this.baseDmgMult * furyMult(this.health.fraction); } // 10.9BF
  private readonly bodyMats: THREE.MeshStandardMaterial[] = [];
  private coreMat!: THREE.MeshStandardMaterial;
  private core!: THREE.Mesh;
  private readonly auraMat: THREE.MeshBasicMaterial;
  private auraPulse = 0;
  private hookRing!: THREE.Mesh;
  private hookRingMat!: THREE.MeshBasicMaterial;

  constructor(
    scene: THREE.Scene,
    private readonly deps: ColossusDeps,
    opts?: ColossusOptions,
  ) {
    // 10.9AP — multiplicadores por instância (acompanha a Ameaça Crescente como os outros bosses)
    this.baseDmgMult = opts?.dmgMult ?? 1;
    this.health = new HealthComponent(Math.round(cfg.maxHp * (opts?.hpMult ?? 1)), undefined, () => this.die());
    this.prevHp = this.health.max;
    this.buildMesh();
    addContactShadow(this.group, this.radius * 2.4, 0.52); // sombra de contato colossal (escala)

    this.auraMat = new THREE.MeshBasicMaterial({ color: ACCENT, transparent: true, opacity: 0.35, depthWrite: false, side: THREE.DoubleSide });
    const aura = new THREE.Mesh(new THREE.RingGeometry(this.radius * 1.5, this.radius * 2.0, 48), this.auraMat);
    aura.rotation.x = -Math.PI / 2;
    aura.position.y = 0.05;
    this.group.add(aura);

    // indicador world-space de HOOK (aparece quando há placa/anchor "hookable")
    this.hookRingMat = new THREE.MeshBasicMaterial({ color: 0x8fe8ff, transparent: true, opacity: 0.9, depthWrite: false, side: THREE.DoubleSide });
    this.hookRing = new THREE.Mesh(new THREE.RingGeometry(this.radius * 0.7, this.radius * 0.95, 6), this.hookRingMat);
    this.hookRing.position.y = this.radius * 3.0;
    this.hookRing.visible = false;
    this.group.add(this.hookRing);

    this.group.position.set(cfg.arena.center.x, 0, cfg.arena.center.z);
    scene.add(this.group);
    events.emit("boss:spawned", undefined);
  }

  // ---- modelo procedural (colossal, mineral, ancestral, ASSIMÉTRICO) ----
  private buildMesh(): void {
    const r = this.radius;
    const rock = new THREE.MeshStandardMaterial({ color: 0x4a5a72, emissive: 0x1a2740, emissiveIntensity: 0.25, roughness: 0.95, flatShading: true });
    const rock2 = new THREE.MeshStandardMaterial({ color: 0x39485f, roughness: 1, flatShading: true });
    const rock3 = new THREE.MeshStandardMaterial({ color: 0x566780, roughness: 1, flatShading: true });
    // 10.5X — PEDRA de verdade: relevo rachado nas placas do colosso
    applySkin(rock, "stone", 0.07, 5);
    applySkin(rock2, "stone", 0.06, 4);
    applySkin(rock3, "stone", 0.05, 4);
    this.bodyMats.push(rock, rock2, rock3);
    // 10.9BT — luz de borda na cor da variante (o Colosso não tem tint por instância: usa o ACCENT)
    for (const m of [rock, rock2, rock3]) applyRim(m, { color: ACCENT, power: 2.4, strength: 1.0 });

    // torso mineral pesado (tapered), levemente inclinado (cabeça baixa à frente)
    const torso = new THREE.Mesh(new THREE.CylinderGeometry(r * 1.35, r * 1.7, r * 2.6, 7), rock);
    torso.rotation.x = Math.PI / 2;
    torso.rotation.z = 0.08; // leve inclinação assimétrica
    torso.position.set(0, r * 1.5, -r * 0.1);
    torso.castShadow = true;
    this.group.add(torso);
    // quadris/anca traseira (mais estreita que os ombros)
    const hips = new THREE.Mesh(new THREE.BoxGeometry(r * 1.7, r * 1.3, r * 1.2), rock2);
    hips.position.set(0, r * 1.35, -r * 1.2);
    hips.castShadow = true;
    this.group.add(hips);

    // OMBROS ASSIMÉTRICOS (o esquerdo maior)
    const shL = new THREE.Mesh(new THREE.DodecahedronGeometry(r * 1.05, 0), rock3);
    shL.position.set(-r * 1.15, r * 2.0, r * 0.5);
    shL.scale.set(1.15, 1, 1.1);
    shL.castShadow = true;
    this.group.add(shL);
    const shR = new THREE.Mesh(new THREE.DodecahedronGeometry(r * 0.82, 0), rock3);
    shR.position.set(r * 1.1, r * 1.95, r * 0.5);
    shR.castShadow = true;
    this.group.add(shR);

    // pescoço protegido + cabeça baixa e larga
    const neck = new THREE.Mesh(new THREE.BoxGeometry(r * 1.0, r * 0.8, r * 0.9), rock2);
    neck.position.set(0, r * 1.55, r * 1.15);
    this.group.add(neck);
    const head = new THREE.Mesh(new THREE.BoxGeometry(r * 1.5, r * 0.85, r * 1.2), rock);
    head.position.set(0, r * 1.15, r * 1.9);
    head.rotation.x = 0.18; // olhando pra baixo
    head.castShadow = true;
    this.group.add(head);
    // maxilar
    const jaw = new THREE.Mesh(new THREE.BoxGeometry(r * 1.2, r * 0.35, r * 0.9), rock2);
    jaw.position.set(0, r * 0.78, r * 2.05);
    this.group.add(jaw);

    // CHIFRES cristalinos marcantes (par grande + um menor assimétrico)
    const hornMat = new THREE.MeshStandardMaterial({ color: ACCENT, emissive: ACCENT, emissiveIntensity: 0.9, roughness: 0.3, flatShading: true });
    this.bodyMats.push(hornMat);
    const bigHorn = (hx: number, s: number, tilt: number): void => {
      const horn = new THREE.Mesh(new THREE.ConeGeometry(r * 0.22 * s, r * 1.5 * s, 5), hornMat);
      horn.position.set(hx, r * 1.7, r * 1.5);
      horn.rotation.set(-0.7, 0, tilt);
      horn.castShadow = true;
      this.group.add(horn);
    };
    bigHorn(-r * 0.55, 1.15, 0.35);
    bigHorn(r * 0.55, 0.95, -0.3); // assimétrico (direito menor)
    // pequenos cristais na testa
    for (const cx of [-r * 0.2, r * 0.2]) {
      const c = new THREE.Mesh(new THREE.OctahedronGeometry(r * 0.2, 0), hornMat);
      c.position.set(cx, r * 1.55, r * 2.0);
      c.scale.set(0.5, 1.1, 0.5);
      this.group.add(c);
    }
    // olhos de energia
    const eyeMat = new THREE.MeshBasicMaterial({ color: 0xd6fbff });
    for (const ex of [-r * 0.32, r * 0.32]) {
      const eye = new THREE.Mesh(new THREE.SphereGeometry(r * 0.13, 6, 6), eyeMat);
      eye.position.set(ex, r * 1.2, r * 2.35);
      this.group.add(eye);
    }

    // placas dorsais em SEQUÊNCIA (tamanho decrescente) — assimetria: uma quebrada
    const dorsalMat = new THREE.MeshStandardMaterial({ color: 0x62e8ff, emissive: ACCENT, emissiveIntensity: 0.5, roughness: 0.4, flatShading: true });
    this.bodyMats.push(dorsalMat);
    const dz = [r * 0.8, r * 0.2, -r * 0.4, -r * 1.0];
    for (let i = 0; i < dz.length; i++) {
      const s = 1 - i * 0.15;
      const fin = new THREE.Mesh(new THREE.ConeGeometry(r * 0.5 * s, r * 0.9 * s, 4), dorsalMat);
      fin.position.set(i === 2 ? r * 0.12 : 0, r * 2.4, dz[i]); // 3ª levemente deslocada
      fin.rotation.set(-0.2, i === 2 ? 0.4 : 0, i === 2 ? 0.5 : 0); // uma "quebrada"/torta
      fin.scale.y = i === 2 ? 0.6 : 1;
      fin.castShadow = true;
      this.group.add(fin);
    }

    // patas DIANTEIRAS maiores que as traseiras (peso à frente)
    const legFront = new THREE.BoxGeometry(r * 0.75, r * 1.35, r * 0.75);
    const legBack = new THREE.BoxGeometry(r * 0.55, r * 1.05, r * 0.55);
    for (const lx of [-r * 0.95, r * 0.95]) {
      const leg = new THREE.Mesh(legFront, rock2);
      leg.position.set(lx, r * 0.68, r * 1.0);
      leg.castShadow = true;
      this.group.add(leg);
      const foot = new THREE.Mesh(new THREE.DodecahedronGeometry(r * 0.42, 0), rock3);
      foot.position.set(lx, r * 0.2, r * 1.15);
      this.group.add(foot);
    }
    for (const lx of [-r * 0.8, r * 0.8]) {
      const leg = new THREE.Mesh(legBack, rock2);
      leg.position.set(lx, r * 0.55, -r * 1.2);
      leg.castShadow = true;
      this.group.add(leg);
    }

    // cauda mineral/cristalina segmentada
    for (let i = 0; i < 3; i++) {
      const seg = new THREE.Mesh(new THREE.DodecahedronGeometry(r * (0.45 - i * 0.1), 0), rock2);
      seg.position.set(0, r * (1.1 - i * 0.2), -r * (1.7 + i * 0.7));
      this.group.add(seg);
    }
    const tailTip = new THREE.Mesh(new THREE.OctahedronGeometry(r * 0.35, 0), hornMat);
    tailTip.position.set(0, r * 0.5, -r * 3.6);
    tailTip.scale.set(0.6, 1.3, 0.6);
    this.group.add(tailTip);

    // NÚCLEO DE AETHER integrado ao peito (exposto na Phase 2)
    this.coreMat = new THREE.MeshStandardMaterial({ color: ACCENT, emissive: ACCENT, emissiveIntensity: 0.5, roughness: 0.3, metalness: 0.3, flatShading: true });
    this.core = new THREE.Mesh(new THREE.IcosahedronGeometry(r * 0.55, 0), this.coreMat);
    this.core.position.set(0, r * 1.5, r * 1.35);
    this.group.add(this.core);
    // moldura de pedra ao redor do núcleo
    const coreFrame = new THREE.Mesh(new THREE.TorusGeometry(r * 0.62, r * 0.14, 6, 8), rock3);
    coreFrame.position.copy(this.core.position);
    this.group.add(coreFrame);

    // CRYSTAL PLATES (3): ombro esq (grande), ombro dir, costas — assimétricas mas legíveis
    const platePos: [number, number, number, number][] = [
      [-r * 1.15, r * 2.4, r * 0.5, 1.15], // left shoulder (maior)
      [r * 1.1, r * 2.35, r * 0.5, 0.95], // right shoulder
      [0, r * 2.15, -r * 0.7, 1.05], // back core
    ];
    for (let i = 0; i < cfg.plates.count; i++) {
      const pg = new THREE.Group();
      const mat = new THREE.MeshStandardMaterial({ color: 0x9ff0ff, emissive: ACCENT, emissiveIntensity: 1.1, roughness: 0.25, metalness: 0.2, flatShading: true });
      for (let k = 0; k < 3; k++) {
        const c = new THREE.Mesh(new THREE.OctahedronGeometry(r * 0.4, 0), mat);
        c.position.set((k - 1) * r * 0.36, k === 1 ? r * 0.22 : 0, 0);
        c.scale.set(0.6, 1.2 + (k === 1 ? 0.3 : 0), 0.6);
        c.rotation.y = k * 0.6;
        pg.add(c);
      }
      const [px, py, pz, sc] = platePos[i];
      pg.position.set(px, py, pz);
      pg.scale.setScalar(sc);
      pg.castShadow = true;
      this.group.add(pg);
      this.plates.push({ group: pg, mat, broken: false });
    }
  }

  // ---- Damageable ----
  get position(): THREE.Vector3 {
    return this.group.position;
  }
  get isDead(): boolean {
    return this.state === "dead";
  }
  get invulnerable(): boolean {
    return this.state === "intro" || this.state === "phaseTransition" || this.state === "dead";
  }
  get armor(): number {
    return 0; // a redução vem das placas (damageReduction)
  }
  /** Crystal Armor: reduz o dano conforme placas VIVAS (DamageSystem lê isto, cap 0.8).
   *  reductionByAlive é ordenado [3 vivas, 2, 1, 0] -> índice = total - vivas. */
  get damageReduction(): number {
    if (this.phase >= 2) return 0; // núcleo exposto: dano normal
    const idx = cfg.plates.count - this.platesAlive;
    return cfg.plates.reductionByAlive[idx] ?? 0;
  }
  onHit(): void {
    if (this.isDead) return;
    this.flash = 0.1;
  }
  applyKnockback(): void {
    /* imóvel */
  }

  // ---- HookTarget ----
  onHooked(): HookReaction {
    // 1) Core Overload: janela de Hook interrompe parcialmente
    if (this.overloadHookTimer > 0) {
      this.interruptOverload();
      return "grab";
    }
    // 2) Placa EXPOSED (após charge na parede): Hook ARRANCA a placa (quebra na hora)
    if (this.exposedTimer > 0 && this.platesAlive > 0) {
      this.exposedTimer = 0;
      this.deps.impulse(0.3);
      this.deps.particles.burst(this.group.position.x, this.radius * 1.6, this.group.position.z, CRACK, { count: 20, speed: 7, dur: 0.6 });
      this.breakPlate(); // recompensa do charge-punish-loop: placa quebra
      this.plateProgress += cfg.plates.hookRip; // e ainda adianta a próxima
      this.resolvePlates();
      return "grab";
    }
    return "none"; // fora de janela: ricochete (não puxa, não trivializa)
  }

  // ---- getters ----
  get hpFraction(): number {
    return Math.max(0, this.health.fraction);
  }
  get phaseNumber(): number {
    return this.phase;
  }
  get nameKey(): string {
    return "boss.aetherColossus";
  }
  private get platesAlive(): number {
    let n = 0;
    for (const p of this.plates) if (!p.broken) n++;
    return n;
  }
  /** Há uma oportunidade de Hook agora? (HUD pulsa o botão) */
  get hookOpportunity(): boolean {
    return this.exposedTimer > 0 || this.overloadHookTimer > 0;
  }
  plateInfo(): PlateInfo {
    return { total: this.plates.length, alive: this.platesAlive, coreExposed: this.phase >= 2 };
  }
  debugSnapshot(): BossDebug {
    return { state: this.state, attack: this.attackName ?? "-", phase: this.phase, hp: Math.ceil(this.health.current) };
  }

  private get atkScale(): number {
    return this.phase >= 2 ? cfg.phase2AttackScale : 1;
  }
  private get speedMult(): number {
    return this.phase >= 2 ? cfg.phase2SpeedMult : 1;
  }

  // ---- loop ----
  update(dt: number): void {
    if (this.state === "dead") {
      this.updateDeath(dt);
      return;
    }
    (Object.keys(this.cd) as ColossusAttack[]).forEach((k) => (this.cd[k] = Math.max(0, this.cd[k] - dt)));
    this.restTimer = Math.max(0, this.restTimer - dt);
    this.trackPlayerVel(dt);
    this.exposedTimer = Math.max(0, this.exposedTimer - dt);
    this.overloadHookTimer = Math.max(0, this.overloadHookTimer - dt);
    if (this.flash > 0) {
      this.flash -= dt;
      for (const m of this.bodyMats) m.emissiveIntensity = this.flash > 0 ? 1.4 : 0.25;
    }
    this.auraPulse += dt * (this.phase >= 2 ? 6 : 3);
    this.auraMat.opacity = 0.3 + Math.sin(this.auraPulse) * 0.15 + (this.phase >= 2 ? 0.15 : 0);
    // núcleo pulsa (mais forte na Phase 2)
    this.coreMat.emissiveIntensity = (this.phase >= 2 ? 1.0 : 0.5) + Math.sin(this.auraPulse * 1.5) * 0.2; // 10.9BT: pico 1.2 (bloom)
    this.core.rotation.y += dt * 1.2;
    // respiração pesada (idle) — sutil, comunica que está vivo/respirando
    this.group.scale.y = 1 + Math.sin(this.auraPulse * (this.phase >= 2 ? 1.1 : 0.7)) * 0.02;
    // indicador de Hook world-space (gira/pulsa quando hookable)
    const hookable = this.hookOpportunity;
    this.hookRing.visible = hookable;
    if (hookable) {
      this.hookRing.rotation.z += dt * 3;
      const s = 1 + Math.sin(this.auraPulse * 6) * 0.15;
      this.hookRing.scale.setScalar(s);
      this.hookRingMat.opacity = 0.7 + Math.sin(this.auraPulse * 6) * 0.3;
    }

    // Crystal Armor: dano normal (HP perdido) alimenta a quebra da placa atual
    if (this.phase === 1 && this.platesAlive > 0) this.trackPlateProgress();
    this.updatePendingZones(dt); // Shard Rain / Overload (impacto após telegraph)

    this.checkPhase();

    switch (this.state) {
      case "intro": this.updateIntro(dt); break;
      case "phaseTransition": this.updateTransition(dt); break;
      case "stagger": this.updateStagger(dt); break;
      case "chase": this.updateChase(dt); break;
      case "attack": this.updateAttack(dt); break;
    }

    this.clampToArena(this.group.position);
    this.updateFacing(dt);
    if (!(this.attackName === "fractureLeap" && this.atkPhase === 1)) this.group.position.y = 0;
    this.group.rotation.y = this.facing;
  }

  private prevHp: number = cfg.maxHp;
  private trackPlateProgress(): void {
    const dmg = Math.max(0, this.prevHp - this.health.current);
    if (dmg > 0) {
      this.plateProgress += dmg;
      this.resolvePlates();
    }
    this.prevHp = this.health.current;
  }

  /** Quebra placas enquanto o progresso acumulado permitir. */
  private resolvePlates(): void {
    while (this.platesAlive > 0 && this.plateProgress >= cfg.plates.hp) {
      this.plateProgress -= cfg.plates.hp;
      this.breakPlate();
    }
    // todas quebradas em P1 -> stagger curto + segue para o núcleo (Phase 2 vem por HP)
    if (this.platesAlive === 0 && !this.staggered && this.phase === 1) {
      this.staggered = true;
      this.enterStagger(cfg.staggerDuration);
    }
  }

  private breakPlate(): void {
    const p = this.plates.find((x) => !x.broken);
    if (!p) return;
    p.broken = true;
    p.group.visible = false;
    this.deps.particles.burst(this.group.position.x, this.radius * 1.8, this.group.position.z, CRACK, { count: 18, speed: 7, dur: 0.6 });
    this.deps.impulse(0.2);
    events.emit("boss:crystalBreak", undefined);
  }

  private updateIntro(dt: number): void {
    this.stateTimer -= dt;
    if (Math.random() < dt * 3) this.deps.particles.burst(this.group.position.x, this.radius * 1.8, this.group.position.z, ACCENT, { count: 8, speed: 3, dur: 0.6 });
    if (this.stateTimer <= 0) this.state = "chase";
  }

  private checkPhase(): void {
    if (this.state === "intro" || this.state === "phaseTransition" || this.state === "dead") return;
    if (this.phase < 2 && this.health.fraction <= cfg.phase2At) this.enterPhase2();
  }

  private enterPhase2(): void {
    this.phase = 2;
    this.state = "phaseTransition";
    this.stateTimer = 1.4;
    this.deps.telegraph.cancel(this.atkTeleId);
    this.atkTeleId = 0;
    this.attackName = null;
    this.atkPhase = 0;
    this.exposedTimer = 0;
    // quebra placas remanescentes visualmente (sem recompensa de Hook)
    for (const p of this.plates) {
      if (!p.broken) {
        p.broken = true;
        p.group.visible = false;
      }
    }
    this.deps.impulse(0.3);
    this.deps.particles.burst(this.group.position.x, this.radius * 1.8, this.group.position.z, ACCENT, { count: 26, speed: 8, dur: 0.9 });
    events.emit("boss:phase", { phase: 2 });
  }

  private updateTransition(dt: number): void {
    this.stateTimer -= dt;
    this.faceInstant(this.deps.player.position);
    if (this.stateTimer <= 0) {
      this.state = "chase";
      this.restTimer = 0.4;
    }
  }

  private enterStagger(dur: number): void {
    this.state = "stagger";
    this.stateTimer = dur;
    this.exposedTimer = Math.min(dur, cfg.plates.exposeWindow); // janela de Hook (se ainda houver placa)
    this.deps.telegraph.cancel(this.atkTeleId);
    this.atkTeleId = 0;
    this.attackName = null;
    this.atkPhase = 0;
    this.deps.particles.burst(this.group.position.x, this.radius * 1.4, this.group.position.z, CRACK, { count: 10, speed: 4, dur: 0.5 });
  }

  private updateStagger(dt: number): void {
    this.stateTimer -= dt;
    if (this.stateTimer <= 0) {
      this.state = "chase";
      this.restTimer = 0.5;
    }
  }

  // ---- chase / seleção ----
  private updateChase(dt: number): void {
    const dist = this.distToPlayer();
    // 10.9AB — CATCH-UP: o colosso anda a 3.2 e o player a 5. De longe ele acelera pra alcançar
    // (senão fica "parado" atrás de quem foge); de perto mantém o passo pesado normal.
    const speed = cfg.moveSpeed * this.speedMult + (dist > 8 ? cfg.moveSpeed * 0.9 : 0);
    if (dist > 4) {
      _v.subVectors(this.deps.player.position, this.group.position);
      _v.y = 0;
      _v.normalize();
      this.group.position.addScaledVector(_v, speed * dt);
      // poeira de passo pesado (escala/peso) — barata, ocasional
      if (Math.random() < dt * 3.5) {
        const fx = this.group.position.x + (Math.random() - 0.5) * this.radius * 1.5;
        const fz = this.group.position.z + this.radius * 0.8;
        this.deps.particles.burst(fx, 0.15, fz, 0x6b7a92, { count: 2, speed: 1.5, dur: 0.5 });
      }
    }
    if (this.restTimer > 0) return;
    const atk = this.selectAttack(dist);
    if (atk) this.beginAttack(atk);
  }

  private ready(a: ColossusAttack): boolean {
    return this.cd[a] <= 0;
  }

  private selectAttack(dist: number): ColossusAttack | null {
    const pool: ColossusAttack[] = [];
    if (this.phase === 1) {
      if (this.ready("fractureCharge") && dist > 6) pool.push("fractureCharge");
      if (this.ready("stoneSweep") && dist < cfg.attacks.stoneSweep.range + 1) pool.push("stoneSweep");
      if (this.ready("shardRain")) pool.push("shardRain");
      if (this.ready("corePulse") && dist < 11) pool.push("corePulse");
    } else {
      if (this.ready("coreOverload")) pool.push("coreOverload");
      if (this.ready("fractureLeap") && dist > 5) pool.push("fractureLeap");
      if (this.ready("prismaticLine")) pool.push("prismaticLine");
      if (this.ready("fractureCharge") && dist > 6) pool.push("fractureCharge");
      if (this.ready("stoneSweep") && dist < cfg.attacks.stoneSweep.range + 1) pool.push("stoneSweep");
      if (this.ready("shardRain")) pool.push("shardRain");
    }
    if (!pool.length) return null;
    return pool[Math.floor(Math.random() * pool.length)];
  }

  private beginAttack(name: ColossusAttack): void {
    this.attackName = name;
    this.state = "attack";
    this.atkPhase = 0;
    this.faceInstant(this.deps.player.position);
    const a = cfg.attacks;
    const s = this.atkScale;
    switch (name) {
      case "stoneSweep":
        this.atkTimer = a.stoneSweep.windup * s;
        this.atkTeleId = this.deps.telegraph.circle(this.group.position.x, this.group.position.z, a.stoneSweep.range, a.stoneSweep.windup * s, ACCENT);
        break;
      case "fractureCharge": {
        this.atkTimer = a.fractureCharge.telegraph * s;
        // 10.9AB — aponta pra onde o player estará no meio do trajeto (cruza o caminho de quem foge)
        this.predictPlayer((a.fractureCharge.maxDist / a.fractureCharge.speed) * 0.5, _tmp);
        this.chargeDir.subVectors(_tmp, this.group.position).setY(0).normalize();
        this.chargeTravelled = 0;
        this.chargeHitDone = false;
        const angle = Math.atan2(this.chargeDir.x, this.chargeDir.z);
        this.atkTeleId = this.deps.telegraph.lane(this.group.position.x, this.group.position.z, angle, a.fractureCharge.maxDist, this.radius * 2, a.fractureCharge.telegraph * s, ACCENT);
        break;
      }
      case "shardRain":
        this.atkTimer = a.shardRain.windup * s;
        break;
      case "corePulse":
        this.atkTimer = a.corePulse.windup * s;
        this.pulseRadius = 0;
        this.atkTeleId = this.deps.telegraph.circle(this.group.position.x, this.group.position.z, a.corePulse.maxRadius, a.corePulse.windup * s, ACCENT);
        break;
      case "fractureLeap":
        this.atkTimer = a.fractureLeap.windup * s;
        // 10.9AB — cai onde o player VAI estar (windup+airTime), não na posição atual (senão erra quem foge)
        this.predictPlayer((a.fractureLeap.windup + a.fractureLeap.airTime) * s * 0.9, this.targetPos);
        this.atkTeleId = this.deps.telegraph.circle(this.targetPos.x, this.targetPos.z, a.fractureLeap.radius, (a.fractureLeap.windup + a.fractureLeap.airTime) * s, ACCENT);
        break;
      case "prismaticLine": {
        this.atkTimer = a.prismaticLine.windup * s;
        // 10.9AB — mira antecipada pelo tempo de windup (o raio dispara ao fim dele)
        this.predictPlayer(a.prismaticLine.windup * s, _tmp);
        this.chargeDir.subVectors(_tmp, this.group.position).setY(0).normalize();
        const ang = Math.atan2(this.chargeDir.x, this.chargeDir.z);
        this.atkTeleId = this.deps.telegraph.lane(this.group.position.x, this.group.position.z, ang, a.prismaticLine.length, a.prismaticLine.width, a.prismaticLine.windup * s, 0xd6a0ff);
        break;
      }
      case "coreOverload":
        this.atkTimer = a.coreOverload.channel;
        this.overloadHookTimer = a.coreOverload.anchorHookWindow;
        this.overloadZonesLeft = a.coreOverload.zones;
        this.overloadZoneTimer = a.coreOverload.zoneInterval;
        this.deps.particles.burst(this.group.position.x, this.radius * 1.8, this.group.position.z, ACCENT, { count: 14, speed: 4, dur: 0.7 });
        break;
    }
  }

  private updateAttack(dt: number): void {
    const name = this.attackName!;
    switch (name) {
      case "stoneSweep": this.runStoneSweep(dt); break;
      case "fractureCharge": this.runCharge(dt); break;
      case "shardRain": this.runShardRain(dt); break;
      case "corePulse": this.runCorePulse(dt); break;
      case "fractureLeap": this.runLeap(dt); break;
      case "prismaticLine": this.runPrismatic(dt); break;
      case "coreOverload": this.runOverload(dt); break;
    }
  }

  private finishAttack(name: ColossusAttack, recovery: number): void {
    this.cd[name] = (cfg.attacks[name] as { cooldown: number }).cooldown;
    this.attackName = null;
    this.atkPhase = 0;
    this.state = "chase";
    this.restTimer = (this.phase >= 2 ? cfg.restRage : cfg.restBetweenAttacks) + recovery;
  }

  // ---- P1 ataques ----
  private runStoneSweep(dt: number): void {
    const a = cfg.attacks.stoneSweep;
    this.atkTimer -= dt;
    if (this.atkPhase === 0 && this.atkTimer <= 0) {
      // impacto: arco amplo à frente
      this.hitArc(a.damage, a.arc, a.range, a.knockback);
      this.deps.impulse(0.15);
      this.deps.particles.burst(this.group.position.x, 0.3, this.group.position.z, ACCENT, { count: 12, speed: 6, dur: 0.5 });
      this.atkPhase = 1;
      this.atkTimer = a.recovery;
    } else if (this.atkPhase === 1 && this.atkTimer <= 0) {
      this.finishAttack("stoneSweep", 0);
    }
  }

  private runCharge(dt: number): void {
    const a = cfg.attacks.fractureCharge;
    if (this.atkPhase === 0) {
      this.atkTimer -= dt;
      if (this.atkTimer <= 0) {
        this.atkPhase = 1; // dash
        this.atkTeleId = 0;
      }
      return;
    }
    if (this.atkPhase === 1) {
      const step = a.speed * dt;
      _v.copy(this.chargeDir).multiplyScalar(step);
      _tmp.copy(this.group.position).add(_v);
      this.chargeTravelled += step;
      // dano por contato
      if (!this.chargeHitDone && this.distToPlayer() <= this.radius + 1.5 && !this.deps.player.invulnerable) {
        dealDamage(this.deps.player, { baseDamage: a.damage * this.dmgMult + this.chip(), critChance: 0, critDamageMult: 1, armor: 0 }, { targetKind: "player", attackerRank: "boss", damageType: "blunt", knockbackFrom: this.group.position, knockbackForce: a.knockback });
        this.chargeHitDone = true;
      }
      // rastro
      if (Math.random() < 0.5) this.deps.particles.burst(this.group.position.x, 0.3, this.group.position.z, ACCENT, { count: 3, speed: 3, dur: 0.35 });
      // bateu na borda da arena -> STAGGER + placa EXPOSED (janela de Hook)
      if (this.wouldExitArena(_tmp)) {
        this.deps.impulse(0.35);
        this.deps.particles.burst(this.group.position.x, this.radius, this.group.position.z, CRACK, { count: 20, speed: 8, dur: 0.7 });
        this.cd.fractureCharge = a.cooldown;
        this.enterStagger(a.wallStagger);
        return;
      }
      this.group.position.copy(_tmp);
      if (this.chargeTravelled >= a.maxDist) {
        this.atkPhase = 2;
        this.atkTimer = a.recovery;
      }
      return;
    }
    // recovery
    this.atkTimer -= dt;
    if (this.atkTimer <= 0) this.finishAttack("fractureCharge", 0);
  }

  private runShardRain(dt: number): void {
    const a = cfg.attacks.shardRain;
    if (this.atkPhase === 0) {
      this.atkTimer -= dt;
      if (this.atkTimer <= 0) {
        this.atkPhase = 1;
        this.shardZonesLeft = a.zonesMin + Math.floor(Math.random() * (a.zonesMax - a.zonesMin + 1));
        this.shardTimer = 0;
      }
      return;
    }
    // fase 1: solta zonas ao longo do tempo (distribuídas, com espaço p/ escapar)
    this.shardTimer -= dt;
    if (this.shardTimer <= 0 && this.shardZonesLeft > 0) {
      this.shardZonesLeft--;
      this.shardTimer = 0.28;
      const ang = Math.random() * Math.PI * 2;
      const rr = Math.random() * (a.spread);
      const zx = this.deps.player.position.x + Math.cos(ang) * rr;
      const zz = this.deps.player.position.z + Math.sin(ang) * rr;
      this.spawnShardZone(zx, zz, a.radius, a.zoneDelay, a.damage);
    }
    if (this.shardZonesLeft <= 0 && this.shardTimer <= 0) this.finishAttack("shardRain", 0.4);
  }

  /** Zona de Shard Rain: telegraph BESPOKE (rachadura→energia→cristal emerge) -> impacto. Pooled. */
  private spawnShardZone(x: number, z: number, radius: number, delay: number, damage: number): void {
    this.deps.telegraph.shard(x, z, radius, delay, ACCENT);
    // impacto agendado via um "temporizador" simples: usamos partículas no fim (o dano é resolvido
    // por um mini-timer no próprio boss não escala; então resolvemos com setTimeout do relógio do jogo
    // seria ruim — em vez disso, checamos no update via lista). Mantemos leve com um closure em fila.
    this.pendingZones.push({ x, z, radius, t: delay, damage });
  }
  private readonly pendingZones: { x: number; z: number; radius: number; t: number; damage: number }[] = [];

  private runCorePulse(dt: number): void {
    const a = cfg.attacks.corePulse;
    if (this.atkPhase === 0) {
      this.atkTimer -= dt;
      if (this.atkTimer <= 0) {
        this.atkPhase = 1;
        this.pulseRadius = 0;
        this.deps.impulse(0.2);
        // onda visual BESPOKE sincronizada ao anel de dano (mesma velocidade/tempo)
        this.deps.telegraph.wave(this.group.position.x, this.group.position.z, a.maxRadius, a.maxRadius / a.waveSpeed, ACCENT);
      }
      return;
    }
    // onda que cresce do boss p/ fora
    this.pulseRadius += a.waveSpeed * dt;
    const d = this.distToPlayer();
    if (!this.deps.player.invulnerable && Math.abs(d - this.pulseRadius) < 1.4 && this.pulseRadius > 2) {
      dealDamage(this.deps.player, { baseDamage: a.damage * this.dmgMult + this.chip(), critChance: 0, critDamageMult: 1, armor: 0 }, { targetKind: "player", attackerRank: "boss", damageType: "blunt", knockbackFrom: this.group.position, knockbackForce: a.knockback });
    }
    if (Math.random() < 0.6) {
      const ang = Math.random() * Math.PI * 2;
      this.deps.particles.burst(this.group.position.x + Math.cos(ang) * this.pulseRadius, 0.3, this.group.position.z + Math.sin(ang) * this.pulseRadius, ACCENT, { count: 2, speed: 2, dur: 0.3 });
    }
    if (this.pulseRadius >= a.maxRadius) this.finishAttack("corePulse", a.recovery);
  }

  // ---- P2 ataques ----
  private runLeap(dt: number): void {
    const a = cfg.attacks.fractureLeap;
    if (this.atkPhase === 0) {
      this.atkTimer -= dt;
      if (this.atkTimer <= 0) {
        this.atkPhase = 1;
        this.atkTimer = a.airTime;
        this.leapStart.copy(this.group.position);
        this.leapT = 0;
      }
      return;
    }
    if (this.atkPhase === 1) {
      this.leapT += dt / a.airTime;
      const t = Math.min(1, this.leapT);
      this.group.position.lerpVectors(this.leapStart, this.targetPos, t);
      this.group.position.y = Math.sin(t * Math.PI) * 6; // arco
      if (t >= 1) {
        this.group.position.y = 0;
        this.hitAoE(this.targetPos, a.radius, a.damage, a.knockback);
        this.deps.impulse(0.35);
        this.deps.particles.burst(this.targetPos.x, 0.3, this.targetPos.z, ACCENT, { count: 20, speed: 8, dur: 0.6 });
        this.atkPhase = 2;
        this.atkTimer = a.recovery;
      }
      return;
    }
    this.atkTimer -= dt;
    if (this.atkTimer <= 0) this.finishAttack("fractureLeap", 0);
  }

  private runPrismatic(dt: number): void {
    const a = cfg.attacks.prismaticLine;
    if (this.atkPhase === 0) {
      this.atkTimer -= dt;
      if (this.atkTimer <= 0) {
        // varredura/dispara: dano ao longo do corredor
        this.hitLane(this.chargeDir, a.length, a.width, a.damage);
        this.deps.impulse(0.2);
        this.atkTeleId = 0;
        this.atkPhase = 1;
        this.atkTimer = a.sweep;
      }
      return;
    }
    this.atkTimer -= dt;
    if (this.atkTimer <= 0) this.finishAttack("prismaticLine", 0.3);
  }

  private runOverload(dt: number): void {
    const a = cfg.attacks.coreOverload;
    this.atkTimer -= dt;
    // solta zonas ao redor durante a canalização (com espaço p/ mover)
    this.overloadZoneTimer -= dt;
    if (this.overloadZoneTimer <= 0 && this.overloadZonesLeft > 0) {
      this.overloadZonesLeft--;
      this.overloadZoneTimer = a.zoneInterval;
      const ang = Math.random() * Math.PI * 2;
      const rr = 3 + Math.random() * 8;
      this.spawnShardZone(this.group.position.x + Math.cos(ang) * rr, this.group.position.z + Math.sin(ang) * rr, a.radius, 0.8, a.damage);
    }
    if (this.atkTimer <= 0) this.finishAttack("coreOverload", 0.6);
  }

  private interruptOverload(): void {
    this.overloadHookTimer = 0;
    this.overloadZonesLeft = Math.floor(this.overloadZonesLeft / 2); // reduz o perigo restante
    this.deps.particles.burst(this.group.position.x, this.radius * 1.6, this.group.position.z, CRACK, { count: 16, speed: 6, dur: 0.5 });
    this.deps.impulse(0.25);
    this.enterStagger(cfg.attacks.coreOverload.interruptStagger);
  }

  // ---- resolução de zonas pendentes (Shard Rain / Overload) ----
  private updatePendingZones(dt: number): void {
    for (let i = this.pendingZones.length - 1; i >= 0; i--) {
      const z = this.pendingZones[i];
      z.t -= dt;
      if (z.t <= 0) {
        this.pendingZones.splice(i, 1);
        this.deps.particles.burst(z.x, 0.3, z.z, ACCENT, { count: 10, speed: 6, dur: 0.5 });
        const p = this.deps.player.position;
        if (!this.deps.player.invulnerable && Math.hypot(p.x - z.x, p.z - z.z) <= z.radius) {
          dealDamage(this.deps.player, { baseDamage: z.damage * this.dmgMult + this.chip(), critChance: 0, critDamageMult: 1, armor: 0 }, { targetKind: "player", attackerRank: "boss", damageType: "blunt", knockbackFrom: new THREE.Vector3(z.x, 0, z.z), knockbackForce: 4 });
        }
      }
    }
  }

  // ---- helpers de dano/posição ----
  /** 10.9AC — fatia da vida MÁX do player somada a cada golpe (dano fixo virava cosquinha no gear alto). */
  private chip(): number {
    return this.deps.player.health.max * cfg.hpChipPct;
  }
  private hitArc(damage: number, arc: number, range: number, knockback: number): void {
    const p = this.deps.player;
    if (p.invulnerable) return;
    _v.subVectors(p.position, this.group.position);
    _v.y = 0;
    const d = _v.length();
    if (d > range + 0.6) return;
    const dot = (_v.x / d) * Math.sin(this.facing) + (_v.z / d) * Math.cos(this.facing);
    if (dot < Math.cos(arc / 2)) return;
    dealDamage(p, { baseDamage: damage * this.dmgMult + this.chip(), critChance: 0, critDamageMult: 1, armor: 0 }, { targetKind: "player", attackerRank: "boss", damageType: "blunt", knockbackFrom: this.group.position, knockbackForce: knockback });
  }
  private hitLane(dir: THREE.Vector3, length: number, width: number, damage: number): void {
    const p = this.deps.player;
    if (p.invulnerable) return;
    _v.subVectors(p.position, this.group.position);
    _v.y = 0;
    const along = _v.x * dir.x + _v.z * dir.z;
    if (along < 0 || along > length) return;
    const perp = Math.abs(_v.x * dir.z - _v.z * dir.x);
    if (perp > width / 2 + 0.5) return;
    dealDamage(p, { baseDamage: damage * this.dmgMult + this.chip(), critChance: 0, critDamageMult: 1, armor: 0 }, { targetKind: "player", attackerRank: "boss", damageType: "blunt", knockbackFrom: this.group.position, knockbackForce: 6 });
  }
  private hitAoE(center: THREE.Vector3, radius: number, damage: number, knockback: number): void {
    const p = this.deps.player;
    if (p.invulnerable) return;
    if (Math.hypot(p.position.x - center.x, p.position.z - center.z) <= radius) {
      dealDamage(p, { baseDamage: damage * this.dmgMult + this.chip(), critChance: 0, critDamageMult: 1, armor: 0 }, { targetKind: "player", attackerRank: "boss", damageType: "blunt", knockbackFrom: center, knockbackForce: knockback });
    }
  }

  private distToPlayer(): number {
    return Math.hypot(this.deps.player.position.x - this.group.position.x, this.deps.player.position.z - this.group.position.z);
  }
  // 10.9AB — velocidade suavizada do player + previsão (leap/charge/prismatic acertam quem foge)
  private trackPlayerVel(dt: number): void {
    const p = this.deps.player.position;
    if (this.velInit && dt > 0) {
      const k = Math.min(1, dt * 8);
      this.playerVel.x += ((p.x - this.lastPlayerPos.x) / dt - this.playerVel.x) * k;
      this.playerVel.z += ((p.z - this.lastPlayerPos.z) / dt - this.playerVel.z) * k;
    }
    this.lastPlayerPos.copy(p);
    this.velInit = true;
  }
  private predictPlayer(t: number, out: THREE.Vector3): THREE.Vector3 {
    const p = this.deps.player.position;
    out.set(p.x + this.playerVel.x * t, 0, p.z + this.playerVel.z * t);
    this.clampToArena(out);
    return out;
  }
  private updateFacing(dt: number): void {
    if (this.state === "dead") return;
    _v.subVectors(this.deps.player.position, this.group.position);
    _v.y = 0;
    if (_v.lengthSq() > 0.01) this.facing = dampAngle(this.facing, Math.atan2(_v.x, _v.z), 6, dt);
  }
  private faceInstant(pos: THREE.Vector3): void {
    _v.subVectors(pos, this.group.position);
    _v.y = 0;
    if (_v.lengthSq() > 0.01) this.facing = Math.atan2(_v.x, _v.z);
  }
  private clampToArena(pos: THREE.Vector3): void {
    const c = cfg.arena.center;
    const dx = pos.x - c.x;
    const dz = pos.z - c.z;
    const lim = cfg.arena.radius + GameConfig.boss.leash.margin - this.radius; // 10.9DA — segue o player pela arena inteira
    const d = Math.hypot(dx, dz);
    if (d > lim && d > 1e-4) {
      pos.x = c.x + (dx / d) * lim;
      pos.z = c.z + (dz / d) * lim;
    }
  }
  private wouldExitArena(pos: THREE.Vector3): boolean {
    const c = cfg.arena.center;
    return Math.hypot(pos.x - c.x, pos.z - c.z) >= cfg.arena.radius - this.radius;
  }

  private die(): void {
    if (this.state === "dead") return;
    this.state = "dead";
    this.deathTimer = 3.2;
    this.deps.telegraph.cancel(this.atkTeleId);
    this.pendingZones.length = 0;
    this.overloadHookTimer = 0;
    this.exposedTimer = 0;
    events.emit("boss:defeatedColossus", undefined);
  }

  private updateDeath(dt: number): void {
    this.deathTimer -= dt;
    const t = Math.max(0, this.deathTimer / 3.2);
    this.coreMat.emissiveIntensity = 2.5 * (1 - t) + 0.5;
    if (Math.random() < dt * 6) this.deps.particles.burst(this.group.position.x + (Math.random() - 0.5) * this.radius * 2, this.radius * (0.5 + Math.random()), this.group.position.z + (Math.random() - 0.5) * this.radius * 2, ACCENT, { count: 6, speed: 5, dur: 0.6 });
    this.group.position.y = -(1 - t) * 2; // afunda ao cair
    this.group.rotation.z = (1 - t) * 0.3;
    if (this.deathTimer <= 0) this.removeRequested = true;
  }

  dispose(scene: THREE.Scene): void {
    this.deps.telegraph.cancel(this.atkTeleId);
    scene.remove(this.group);
    this.group.traverse((o) => {
      if (o instanceof THREE.Mesh) o.geometry?.dispose?.();
    });
    for (const m of this.bodyMats) m.dispose();
    this.coreMat.dispose();
    this.auraMat.dispose();
    this.hookRingMat.dispose();
  }
}
